import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import TiltCard from "@/components/TiltCard";

const medalStyles = {
  ouro: "from-amber-500/20 to-[#102944]",
  prata: "from-slate-300/15 to-[#193454]",
  bronze: "from-orange-500/15 to-[#17243e]",
};

function getInitials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

export default function HallOfFameGallery({ limit }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get("/public/hall-of-fame");
      setItems(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <p role="status" className="text-center text-slate-400">Carregando conquistas…</p>;
  if (error) return <div role="alert" className="text-center text-slate-400">Não foi possível carregar as conquistas.
    <Button variant="outline" onClick={load} className="ml-3">Tentar novamente</Button></div>;
  if (!items.length) return <p className="text-center text-slate-400">Em breve, novas conquistas dos nossos atletas.</p>;

  return <>
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
      {(limit ? items.slice(0, limit) : items).map((item) => {
        const medal = (item.medal || "").toLowerCase();
        const background = medalStyles[medal] || "from-amber-500/15 to-[#102944]";
        return <TiltCard
          key={item.id}
          max={5}
          className={`min-h-[250px] border border-amber-400/15 bg-gradient-to-br ${background} p-6 shadow-[0_18px_42px_rgba(0,0,0,0.22)] transition-[border-color,box-shadow] duration-300 hover:border-amber-400/40 hover:shadow-[0_24px_50px_rgba(0,0,0,0.32)] sm:p-8`}
          data-testid={`public-achievement-${item.id}`}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-amber-400/50 bg-amber-400/15 font-mono text-xl font-semibold text-amber-400" aria-hidden="true">{getInitials(item.athlete_name)}</span>
            <span className="text-right text-[10px] font-bold uppercase tracking-wider text-amber-300">{[item.medal, item.year].filter(Boolean).join(" • ")}</span>
          </div>
          <h3 className="mt-6 text-xl font-semibold text-white">{item.athlete_name}</h3>
          <p className="mt-1 text-sm font-medium text-amber-300">{item.title}</p>
          {(item.competition || item.category) && <p className="mt-4 text-xs text-slate-300">{[item.competition, item.category].filter(Boolean).join(" • ")}</p>}
          {item.description && <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-300">{item.description}</p>}
        </TiltCard>;
      })}
    </div>
    {limit && items.length > limit && <div className="mt-6 text-center"><Link to="/hall-da-fama" className="text-amber-300 hover:underline">Ver todas as conquistas</Link></div>}
  </>;
}
